/**
 * Ad-hoc join builder — combine two of a retired system's tables on a shared key.
 *
 * The saved record view answers "show me the join someone already worked out".
 * This answers "I have a question nobody anticipated", which is the normal case
 * once the source application is gone and there is nobody left to ask.
 *
 * Two design points worth keeping.
 *
 * **Overlap is checked before rows are fetched.** A mistyped key produces an
 * empty grid, and an empty grid in an archive reads as "the records were never
 * archived" rather than "your join is wrong". Reporting that 6,000 base rows
 * found 4,491 matches turns a silent failure into a number.
 *
 * **Left join is the default.** An inner join silently drops base rows with no
 * match — in an archive, dropping records is the one thing that must never
 * happen quietly. Inner is available; it is not the default.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  MultiSelect,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconInfoCircle, IconPlayerPlay } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  createRecordView,
  getArchiveDatasetSchema,
  joinOverlap,
  previewJoin,
  type ArchiveDataset,
  type ArchiveJoinedRows,
  type ArchiveJoinOverlap,
} from "@/shared/api/archive";

type ColumnMeta = { name: string };

function columnNames(schema: { columns?: unknown } | null): string[] {
  const raw = (schema?.columns ?? []) as ColumnMeta[];
  return raw.map((c) => String((c as unknown as Record<string, unknown>).name)).filter(Boolean);
}

export function ArchiveJoinBuilder({
  opened,
  onClose,
  containerId,
  datasets,
  baseDatasetId,
  canSave,
  onSaved,
}: {
  opened: boolean;
  onClose: () => void;
  containerId: string;
  datasets: ArchiveDataset[];
  baseDatasetId: string;
  canSave: boolean;
  onSaved: () => void;
}) {
  const getToken = useAccessToken();

  const [baseId, setBaseId] = useState(baseDatasetId);
  const [joinId, setJoinId] = useState<string | null>(null);
  const [leftColumn, setLeftColumn] = useState<string | null>(null);
  const [rightColumn, setRightColumn] = useState<string | null>(null);
  const [joinType, setJoinType] = useState("left");
  const [picked, setPicked] = useState<string[]>([]);

  const [baseColumns, setBaseColumns] = useState<string[]>([]);
  const [joinColumns, setJoinColumns] = useState<string[]>([]);
  const [overlap, setOverlap] = useState<ArchiveJoinOverlap | null>(null);
  const [rows, setRows] = useState<ArchiveJoinedRows | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setBaseId(baseDatasetId), [baseDatasetId]);

  // Schemas drive every column picker, so a column that does not exist cannot
  // be typed in — the server validates too, but a dropdown prevents the trip.
  useEffect(() => {
    if (!baseId) return;
    void (async () => {
      const token = await getToken();
      setBaseColumns(columnNames(await getArchiveDatasetSchema(token, baseId)));
    })();
  }, [baseId, getToken]);

  useEffect(() => {
    if (!joinId) {
      setJoinColumns([]);
      return;
    }
    void (async () => {
      const token = await getToken();
      setJoinColumns(columnNames(await getArchiveDatasetSchema(token, joinId)));
    })();
  }, [joinId, getToken]);

  // A shared column name is the overwhelmingly common case (case_id to case_id),
  // so offer it rather than making someone find it twice.
  useEffect(() => {
    if (!leftColumn || rightColumn) return;
    if (joinColumns.includes(leftColumn)) setRightColumn(leftColumn);
  }, [leftColumn, rightColumn, joinColumns]);

  const nameOf = useMemo(
    () => (id: string) => datasets.find((d) => d.dataset_id === id)?.dataset_name ?? id,
    [datasets],
  );

  const ready = !!baseId && !!joinId && !!leftColumn && !!rightColumn;

  function body() {
    const columns = picked.length
      ? picked.map((key) => {
          const [datasetId, column] = key.split("::");
          return { dataset_id: datasetId!, column: column!, label: column! };
        })
      : [];
    return {
      container_id: containerId,
      base_dataset_id: baseId,
      joins: [
        {
          dataset_id: joinId!,
          left_column: leftColumn!,
          right_column: rightColumn!,
          join_type: joinType,
        },
      ],
      columns,
      filters: [],
      page: 1,
      page_size: 25,
      q: "",
    };
  }

  async function check() {
    setBusy(true);
    setError(null);
    try {
      const token = await getToken();
      setOverlap(await joinOverlap(token, body()));
      setRows(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const token = await getToken();
      setRows(await previewJoin(token, body()));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    try {
      const token = await getToken();
      await createRecordView(token, containerId, {
        name: `${nameOf(baseId)} with ${nameOf(joinId!)}`,
        description: `Joined on ${leftColumn} = ${rightColumn} (${joinType} join).`,
        base_dataset_id: baseId,
        joins: body().joins,
        columns: body().columns,
      });
      notifications.show({
        title: "Saved as a record view",
        message: "It is now in the record view list for everyone with access to this archive.",
        color: "teal",
      });
      onSaved();
      onClose();
    } catch (err) {
      notifications.show({
        title: "Could not save the view",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  const columnOptions = [
    {
      group: nameOf(baseId),
      items: baseColumns.map((c) => ({ value: `${baseId}::${c}`, label: c })),
    },
    ...(joinId
      ? [
          {
            group: nameOf(joinId),
            items: joinColumns.map((c) => ({ value: `${joinId}::${c}`, label: c })),
          },
        ]
      : []),
  ];

  return (
    <Modal opened={opened} onClose={onClose} title="Join two tables" size="xl">
      <Stack gap="md">
        <Alert variant="light" color="navy" icon={<IconInfoCircle size={18} />}>
          Combine two of this system&apos;s tables on a shared key and see the result as one record.
          Nothing is written — the archived tables are unchanged.
        </Alert>

        <Group grow align="flex-start">
          <Select
            label="Start from"
            value={baseId}
            data={datasets.map((d) => ({ value: d.dataset_id, label: d.dataset_name }))}
            onChange={(value) => {
              setBaseId(value ?? baseDatasetId);
              setLeftColumn(null);
              setOverlap(null);
              setRows(null);
            }}
          />
          <Select
            label="Join with"
            placeholder="Pick a table"
            value={joinId}
            data={datasets
              .filter((d) => d.dataset_id !== baseId)
              .map((d) => ({ value: d.dataset_id, label: d.dataset_name }))}
            onChange={(value) => {
              setJoinId(value);
              setRightColumn(null);
              setOverlap(null);
              setRows(null);
            }}
          />
        </Group>

        <Group grow align="flex-start">
          <Select
            label={`Key in ${nameOf(baseId)}`}
            placeholder="Column"
            searchable
            value={leftColumn}
            data={baseColumns}
            onChange={(value) => {
              setLeftColumn(value);
              setOverlap(null);
            }}
          />
          <Select
            label={joinId ? `Key in ${nameOf(joinId)}` : "Key in the joined table"}
            placeholder="Column"
            searchable
            disabled={!joinId}
            value={rightColumn}
            data={joinColumns}
            onChange={(value) => {
              setRightColumn(value);
              setOverlap(null);
            }}
          />
          <Select
            label="Join type"
            value={joinType}
            data={[
              { value: "left", label: "Left — keep every row" },
              { value: "inner", label: "Inner — only matches" },
            ]}
            onChange={(value) => {
              setJoinType(value ?? "left");
              setOverlap(null);
              setRows(null);
            }}
          />
        </Group>

        <MultiSelect
          label="Columns to show"
          description="Leave empty for all of them"
          placeholder={picked.length ? undefined : "All columns"}
          searchable
          clearable
          value={picked}
          data={columnOptions}
          onChange={setPicked}
        />

        <Group>
          <Button variant="light" disabled={!ready} loading={busy} onClick={() => void check()}>
            Check the key first
          </Button>
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            disabled={!ready}
            loading={busy}
            onClick={() => void run()}
          >
            Show the joined records
          </Button>
        </Group>

        {error && (
          <Alert color="red" variant="light" title="That join did not work">
            {error}
          </Alert>
        )}

        {overlap && (
          <Alert
            variant="light"
            color={overlap.matched === 0 ? "orange" : "teal"}
            icon={
              overlap.matched === 0 ? <IconAlertTriangle size={18} /> : <IconInfoCircle size={18} />
            }
          >
            {overlap.matched === 0 ? (
              <Text size="sm">
                No row in <strong>{overlap.base_dataset}</strong> matches on that key. That is
                almost always the wrong pair of columns rather than an empty archive — check the
                sample values in the schema.
              </Text>
            ) : (
              <Text size="sm">
                <strong>{overlap.matched.toLocaleString()}</strong> of{" "}
                {overlap.base_rows.toLocaleString()} rows in {overlap.base_dataset} find a match.{" "}
                {overlap.unmatched > 0 && (
                  <>
                    {overlap.unmatched.toLocaleString()} do not —{" "}
                    {joinType === "left"
                      ? "a left join keeps them, with the joined columns empty."
                      : "an inner join will drop them."}
                  </>
                )}
              </Text>
            )}
          </Alert>
        )}

        {rows && (
          <Paper withBorder radius="md">
            <Group justify="space-between" p="sm">
              <Text size="sm" fw={500}>
                {rows.total.toLocaleString()} joined records
              </Text>
              {canSave && (
                <Button
                  size="compact-sm"
                  variant="light"
                  loading={busy}
                  onClick={() => void save()}
                >
                  Save as a record view
                </Button>
              )}
            </Group>
            <ScrollArea>
              <Table striped fz="xs" verticalSpacing={5}>
                <Table.Thead>
                  <Table.Tr>
                    {rows.columns.map((c) => (
                      <Table.Th key={String(c.key)}>{String(c.label ?? c.key)}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.rows.slice(0, 25).map((row, i) => (
                    <Table.Tr key={i}>
                      {rows.columns.map((c) => {
                        const value = row[String(c.key)];
                        return (
                          <Table.Td key={String(c.key)}>
                            {value === null || value === undefined || value === "" ? (
                              <Badge size="xs" variant="default" c="dimmed" tt="none">
                                empty
                              </Badge>
                            ) : (
                              String(value)
                            )}
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>
        )}
      </Stack>
    </Modal>
  );
}
